import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffectiveRole } from "@/hooks/useEffectiveRole";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, AlertTriangle, Shield, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { hrvExposureLabel } from "@/lib/questionnaire/hrvSelfV1";
import { HRV_DOMAIN_LABELS } from "@/lib/functions";
import { PREVIEW_ALLOWLIST } from "@/config/featureFlags";
import "@/reports/print.css";

// Shape returned by respondent_get_hrv_assessment_summary RPC
interface HrvRpcDomainScore {
  domain: string;
  label: string;
  score: number;
  exposure_label: string;
}

interface HrvRpcSummary {
  questionnaire_id: string;
  composite_score: number;
  composite_exposure_label: string;
  escalation_required: boolean;
  escalation_reason_code: string | null;
  domain_scores: HrvRpcDomainScore[];
  technical_assessment_recommended: boolean;
  technical_assessment_type: 'full' | 'targeted' | null;
}

interface QuestionRow {
  question_key: string;
  domain: string;
  question_text: string;
  title: string | null;
  score: number | null;
  comment: string | null;
}

const ESCALATION_REASONS: Record<string, string> = {
  HIGH_COMPOSITE: "The overall composite risk score exceeds the high-exposure threshold.",
  OWNERSHIP_DATA_COMBINATION: "High ownership/governance risk combined with elevated data sensitivity risk.",
  CONTROL_SUPPLY_COMBINATION: "High operational control risk combined with high supply chain risk.",
};

const EXPOSURE_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  "Low Exposure": { bg: "bg-green-50 dark:bg-green-950/30", text: "text-green-700 dark:text-green-300", border: "border-green-200 dark:border-green-800" },
  "Moderate Exposure": { bg: "bg-yellow-50 dark:bg-yellow-950/30", text: "text-yellow-700 dark:text-yellow-300", border: "border-yellow-200 dark:border-yellow-800" },
  "High Exposure": { bg: "bg-red-50 dark:bg-red-950/30", text: "text-red-700 dark:text-red-300", border: "border-red-200 dark:border-red-800" },
};

const EXPOSURE_BADGE: Record<string, string> = {
  "Low Exposure": "bg-green-100 text-green-700 border-green-300",
  "Moderate Exposure": "bg-yellow-100 text-yellow-700 border-yellow-300",
  "High Exposure": "bg-red-100 text-red-700 border-red-300",
};

function riskLabelFromScore(score: number): string {
  if (score < 1.50) return "Low Risk";
  if (score < 2.25) return "Moderate Risk";
  return "High Risk";
}

function getNarrative(label: string): string {
  if (label === "High Exposure")
    return "The analysis indicates significant risk exposure across one or more critical dimensions. Escalation to enterprise-level review is strongly recommended prior to any investment or engagement decision. Legal consultation and contractual safeguards should be established.";
  if (label === "Moderate Exposure")
    return "The analysis indicates moderate risk exposure. Enhanced due diligence is recommended, including a targeted technical review of identified risk areas. Standard contractual protections should be reviewed for adequacy.";
  return "The analysis indicates low overall risk exposure. Standard monitoring protocols are sufficient. No escalation is required at this time.";
}

function getActionPlan(label: string, escalation: boolean) {
  if (escalation || label === "High Exposure") {
    return {
      text: "Enhanced due diligence required. Legal/contractual safeguards and deeper technical review recommended.",
      bullets: [
        "Initiate enterprise-level risk review",
        "Engage legal counsel for contractual safeguards",
        "Conduct technical deep-dive assessment",
        "Establish enhanced monitoring protocols",
      ],
    };
  }
  if (label === "Moderate Exposure") {
    return {
      text: "Proceed with targeted follow-up diligence on flagged risk areas.",
      bullets: [
        "Schedule enhanced due diligence review",
        "Request additional documentation from entity",
        "Review existing contractual protections",
      ],
    };
  }
  return {
    text: "Proceed with standard investment due diligence. No escalation required.",
    bullets: [
      "Continue standard monitoring",
      "Periodic reassessment recommended",
    ],
  };
}

const isDev = import.meta.env.DEV;

export default function HrvReportPdf() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { role: effectiveRole } = useEffectiveRole();
  const backPath = effectiveRole === "portfolio_manager" ? "/pm" : "/dashboard";
  const backLabel = effectiveRole === "portfolio_manager" ? "Back to Portfolio Dashboard" : "Back to Dashboard";

  const [summary, setSummary] = useState<HrvRpcSummary | null>(null);
  const [questions, setQuestions] = useState<QuestionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [orgName, setOrgName] = useState("Organization");
  const [userName, setUserName] = useState("");
  const [assessmentStatus, setAssessmentStatus] = useState("Draft");
  const [hasResponses, setHasResponses] = useState(false);

  const [confirmChecked, setConfirmChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isSubmitted = assessmentStatus === "Submitted" || assessmentStatus === "In Review" || assessmentStatus === "Closed";
  const [userEmail, setUserEmail] = useState("");
  const isAllowlisted = PREVIEW_ALLOWLIST.includes(userEmail.toLowerCase());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);

      // Parallel fetch: summary, assessment metadata, responses, items
      const [summaryRes, assessRes, responsesRes] = await Promise.all([
        (supabase.rpc as any)("respondent_get_hrv_assessment_summary", { p_assessment_id: assessmentId }),
        (supabase.rpc as any)("respondent_get_assessment", { p_assessment_id: assessmentId }),
        (supabase.rpc as any)("respondent_get_responses", { p_assessment_id: assessmentId }),
      ]);

      if (cancelled) return;

      // Summary
      let rpcSummary: HrvRpcSummary | null = null;
      if (!summaryRes.error && summaryRes.data) {
        rpcSummary = summaryRes.data as HrvRpcSummary;
        if (isDev) console.log("[HRV PDF] summary:", rpcSummary);
      } else if (summaryRes.error) {
        console.warn("[HRV PDF] summary error:", summaryRes.error);
      }

      // Assessment meta
      let qId: string | null = null;
      if (!assessRes.error && assessRes.data?.length > 0) {
        setOrgName(assessRes.data[0].org_name ?? "Organization");
        setAssessmentStatus(assessRes.data[0].status ?? "Draft");
        qId = assessRes.data[0].questionnaire_id ?? null;
      }

      // User
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) {
        setUserName(session.user.email);
        setUserEmail(session.user.email);
      }

      // Responses
      const responseData = responsesRes.data ?? [];
      setHasResponses(responseData.length > 0);

      // Fetch items for question text
      let itemsData: any[] = [];
      if (qId) {
        const itemsRes = await (supabase.rpc as any)("respondent_get_questionnaire_items", {
          p_assessment_id: assessmentId,
          p_questionnaire_id: qId,
        });
        itemsData = itemsRes.data ?? [];
      }

      // Build question map
      const qMap = new Map<number, { question_key: string; domain: string; title: string | null; question_text: string }>();
      for (const item of itemsData) {
        qMap.set(item.question_id, {
          question_key: item.question_key,
          domain: item.domain,
          title: item.title ?? null,
          question_text: item.question_text ?? "",
        });
      }

      // Merge responses with question metadata
      const questionRows: QuestionRow[] = [];
      for (const r of responseData) {
        const meta = qMap.get(r.question_id);
        if (!meta) continue;
        questionRows.push({
          question_key: meta.question_key,
          domain: meta.domain,
          question_text: meta.question_text,
          title: meta.title,
          score: r.score ?? null,
          comment: r.comment ?? null,
        });
      }
      // Sort by domain order then question_key
      const domainOrder: Record<string, number> = { OG: 0, OC: 1, SC: 2, DS: 3 };
      questionRows.sort((a, b) => (domainOrder[a.domain] ?? 99) - (domainOrder[b.domain] ?? 99) || a.question_key.localeCompare(b.question_key));

      if (!cancelled) {
        setSummary(rpcSummary);
        setQuestions(questionRows);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [assessmentId]);

  const handleSubmit = async () => {
    if (!assessmentId || !confirmChecked) return;
    setSubmitting(true);
    try {
      const { data, error } = await (supabase.rpc as any)("respondent_submit_assessment", {
        p_assessment_id: assessmentId,
      });
      if (error) throw error;
      const result = typeof data === "string" ? JSON.parse(data) : data;
      if (result && result.success === false) throw new Error(result.message || "Submission failed");
      toast({ title: "Assessment submitted successfully.", description: "Your HRV risk assessment is now locked." });
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Submit failed", description: err?.message ?? "Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const todayStr = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  }, []);

  if (loading) {
    return (
      <div className="p-6 print-container">
        <h2 className="text-xl font-semibold">Preparing HRV Risk Report…</h2>
      </div>
    );
  }

  if (!hasResponses) {
    return (
      <div className="p-6 print-container max-w-lg mx-auto mt-20 text-center space-y-4">
        <h2 className="text-xl font-semibold">
          {isAllowlisted ? "Preview Not Available" : "No data to export"}
        </h2>
        <p className="text-muted-foreground">
          {isAllowlisted
            ? "Please select at least one answer to generate a report preview."
            : "Answer at least one question in the assessment, then try again."}
        </p>
        <Button variant="outline" onClick={() => navigate(backPath)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {backLabel}
        </Button>
      </div>
    );
  }

  const s = summary;
  const exposureLabel = s?.composite_exposure_label ?? "Low Exposure";
  const levelStyle = EXPOSURE_STYLES[exposureLabel] ?? EXPOSURE_STYLES["Low Exposure"];
  const isIncomplete = questions.length < 16;
  const actionPlan = getActionPlan(exposureLabel, s?.escalation_required ?? false);

  return (
    <div className="print-root">
      <div className="print-sentinel" aria-hidden="true" />

      {/* PAGE 1 */}
      <div className="print-page-one p-6 space-y-6">
        {/* 1. Header */}
        <div className="border-b pb-4">
          <h1 className="text-2xl font-bold">High-Risk Vendor (HRV) Self-Assessment Report</h1>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-1">
            <span>{orgName}</span>
            {userName && <span>• {userName}</span>}
            <span>• {todayStr}</span>
          </div>
        </div>

        {/* Incomplete warning */}
        {isIncomplete && (
          <div className="border border-yellow-300 dark:border-yellow-700 rounded-lg p-3 bg-yellow-50 dark:bg-yellow-950/30 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <p className="text-sm text-yellow-800 dark:text-yellow-300">
              Incomplete assessment — {questions.length} of 16 questions answered. Results may not reflect full risk profile.
            </p>
          </div>
        )}

        {/* 2. Executive Summary */}
        {s && (
          <Card className={`border-2 ${levelStyle.border}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Executive Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Composite Exposure</p>
                  <Badge className={`text-lg px-3 py-1 ${EXPOSURE_BADGE[exposureLabel] ?? ""}`}>
                    {exposureLabel}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Risk Outcome</p>
                  <p className={`text-base font-semibold ${levelStyle.text}`}>{exposureLabel}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Escalation Required</p>
                  <div className="flex items-center gap-1 mt-1">
                    {s.escalation_required ? (
                      <Badge className="bg-red-100 text-red-700 border-red-300 border">
                        <AlertTriangle className="h-3 w-3 mr-1" /> Yes
                      </Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700 border-green-300 border">
                        <CheckCircle className="h-3 w-3 mr-1" /> No
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              {s.escalation_required && s.escalation_reason_code && (
                <p className="text-sm text-red-600 dark:text-red-400 italic">
                  Escalation reason: {ESCALATION_REASONS[s.escalation_reason_code] ?? s.escalation_reason_code}
                </p>
              )}
              <p className="text-sm text-muted-foreground leading-relaxed">{getNarrative(exposureLabel)}</p>
            </CardContent>
          </Card>
        )}

        {/* 3. HRV Risk Profile + Domain Breakdown */}
        {s && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">HRV Risk Profile</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(s.domain_scores || []).map((ds) => {
                  const dStyle = EXPOSURE_STYLES[ds.exposure_label] ?? EXPOSURE_STYLES["Low Exposure"];
                  const dot = ds.exposure_label === "High Exposure" ? "🔴" : ds.exposure_label === "Moderate Exposure" ? "🟡" : "🟢";
                  const riskLabel = ds.exposure_label === "High Exposure" ? "High Risk" : ds.exposure_label === "Moderate Exposure" ? "Moderate Risk" : "Low Risk";
                  return (
                    <div key={ds.domain} className={`rounded-lg border p-3 ${dStyle.bg} ${dStyle.border}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{dot}</span>
                          <div>
                            <p className="font-medium text-sm">{ds.label}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{ds.domain}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={`border ${EXPOSURE_BADGE[ds.exposure_label] ?? ""}`}>
                            {riskLabel}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 3b. Technical Assessment Recommendation */}
        {s?.technical_assessment_recommended && (
          <Card className={`border-2 ${s.technical_assessment_type === 'full' ? 'border-red-300 dark:border-red-700' : 'border-yellow-300 dark:border-yellow-700'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                🔎 Deeper Technical Assessment Recommended
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {s.technical_assessment_type === 'full'
                  ? 'Based on the HRV exposure profile, a full technical cybersecurity assessment is recommended to evaluate operational controls, vendor integration risk, and infrastructure security posture prior to investment decision-making.'
                  : 'Based on the HRV exposure profile, a targeted technical review is recommended in higher-risk domains to validate control effectiveness and vendor risk mitigation strategies.'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* 4. Action Plan */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">What To Do Next</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className={`rounded-lg border p-3 ${levelStyle.bg} ${levelStyle.border}`}>
              <p className="text-sm font-medium">{actionPlan.text}</p>
            </div>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              {actionPlan.bullets.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* PAGE 2: Responses Table */}
      {questions.length > 0 && (
        <div className="print-page-two p-6 space-y-4">
          <h2 className="text-lg font-bold">Detailed Responses</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-40">Area</TableHead>
                <TableHead>Question</TableHead>
                <TableHead className="w-28">Risk Level</TableHead>
                <TableHead>Comment</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {questions.map((q) => (
                <TableRow key={q.question_key}>
                  <TableCell className="text-xs font-medium">{(HRV_DOMAIN_LABELS as any)[q.domain] ?? q.domain}</TableCell>
                  <TableCell className="text-xs">{q.question_text}</TableCell>
                  <TableCell>
                    {q.score != null ? (
                      <Badge className={`text-xs border ${EXPOSURE_BADGE[hrvExposureLabel(q.score)] ?? ""}`}>
                        {riskLabelFromScore(q.score)}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{q.comment || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Footer */}
      <div className="p-6 text-center text-xs text-muted-foreground print-footer">
        Generated by CSV Pro
      </div>

      {/* Action buttons (hidden in print) */}
      <div className="no-print p-6 pt-4 space-y-4">
        {!isSubmitted && !isAllowlisted && (
          <div className="border border-amber-200 dark:border-amber-800 rounded-lg p-4 bg-amber-50 dark:bg-amber-950/30">
            <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
              ⚠ This report is only available after submission. Please complete and submit your assessment first.
            </p>
            <Button variant="outline" className="mt-3" onClick={() => navigate(`/wizard/${assessmentId}`)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Return to Assessment
            </Button>
          </div>
        )}

        {!isSubmitted && isAllowlisted && (
          <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-50 dark:bg-blue-950/30">
            <p className="text-sm text-blue-800 dark:text-blue-300 font-medium">
              {questions.length >= 16
                ? "🔍 QA Preview Mode — This report reflects a fully completed assessment"
                : "🔍 QA Preview Mode — This report is generated from an assessment that may be partially completed"}
            </p>
          </div>
        )}

        {isSubmitted && (
          <div className="border border-green-200 dark:border-green-800 rounded-lg p-4 bg-green-50 dark:bg-green-950/30">
            <p className="text-sm text-green-800 dark:text-green-300 font-medium">
              ✓ This assessment has been submitted and is now locked.
            </p>
          </div>
        )}

        {(isSubmitted || isAllowlisted) && (
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate(backPath)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {backLabel}
            </Button>
            <Button onClick={() => { setTimeout(() => window.print(), 500); }}>
              Print / Download PDF
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
